import { css } from 'lit';
import { hostReset, placementOrigins } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  placementOrigins,
  css`
    /* ── Host — cascade variant tokens to all child elements ─────────────────── */
    :host {
      display: inline-block;
      vertical-align: middle;

      /* Primary defaults */
      --_bg:        var(--uwc-color-primary, ${t.primary});
      --_color:     ${t.colorWhite};
      --_radius:    var(--uwc-radius-md, ${t.radiusMd});
      --_shadow:    none;
      --_font-size: var(--uwc-font-size-md, ${t.fontSizeMd});
      /* Shared control height so a split button lines up with buttons/inputs at
         every size. --_py stays for the chevron's square padding only; it is
         absorbed by the definite height and never drives the box height. */
      --_height:    var(--uwc-splitbtn-height, ${t.controlHeightMd});
      --_px:        0.875rem;
      --_py:        0.5rem;
      --_gap:       0.375rem;
    }
    :host([fluid]) { display: block; width: 100%; }

    /* ── Variant colors ───────────────────────────────────────────────────────── */
    :host([variant="secondary"]) { --_bg: var(--uwc-color-secondary, ${t.secondary}); }
    :host([variant="success"])   { --_bg: var(--uwc-color-success,   ${t.success}); }
    :host([variant="info"])      { --_bg: var(--uwc-color-info,      ${t.info}); }
    :host([variant="warning"])   { --_bg: var(--uwc-color-warning,   ${t.warning}); --_color: var(--uwc-btn-warning-color, ${t.contrast}); }
    :host([variant="help"])      { --_bg: var(--uwc-color-help,      ${t.help}); }
    :host([variant="danger"])    { --_bg: var(--uwc-color-danger,    ${t.danger}); }
    :host([variant="contrast"])  { --_bg: var(--uwc-color-contrast,  ${t.contrast}); --_color: ${t.colorWhite}; }

    /* ── Sizes ────────────────────────────────────────────────────────────────── */
    :host([size="small"]) { --_font-size: var(--uwc-font-size-xs, ${t.fontSizeXs}); --_height: ${t.controlHeightSm}; --_px: 0.625rem;  --_py: 0.3125rem; }
    :host([size="large"]) { --_font-size: var(--uwc-font-size-lg, ${t.fontSizeLg}); --_height: ${t.controlHeightLg}; --_px: 1.125rem;  --_py: 0.6875rem; }

    /* ── Raised ──────────────────────────────────────────────────────────────── */
    :host([raised]) { --_shadow: 0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12); }

    /* ── Rounded ─────────────────────────────────────────────────────────────── */
    :host([rounded]) { --_radius: var(--uwc-radius-full, ${t.radiusFull}); }

    /* ── Outer wrapper ───────────────────────────────────────────────────────── */
    .uwc-spb {
      display:   inline-flex;
      align-items: stretch;
      position:  relative;
      width:     max-content;
    }
    .uwc-spb--fluid   { width: 100%; }
    .uwc-spb--disabled { pointer-events: none; opacity: 0.6; cursor: not-allowed; }

    /* ── Shared button base ───────────────────────────────────────────────────── */
    .uwc-spb__main,
    .uwc-spb__trigger {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      gap:             var(--_gap);
      height:          var(--_height);
      font-family:     var(--uwc-font-family, inherit);
      font-size:       var(--_font-size);
      font-weight:     var(--uwc-btn-font-weight, ${t.fontWeightMedium});
      line-height:     1.25;
      white-space:     nowrap;
      cursor:          pointer;
      user-select:     none;
      outline:         none;
      border:          1px solid transparent;
      background-color: var(--_bg);
      color:           var(--_color);
      box-shadow:      var(--_shadow);
      transition:
        background-color ${t.durationBase} ease,
        box-shadow       ${t.durationBase} ease,
        color            ${t.durationBase} ease;
      position: relative;
      overflow: hidden;
    }
    .uwc-spb__main:hover,
    .uwc-spb__trigger:hover {
      background-color: color-mix(in oklab, var(--_bg) 100%, white 12%);
    }
    .uwc-spb__main:active,
    .uwc-spb__trigger:active {
      background-color: color-mix(in oklab, var(--_bg) 100%, black 6%);
    }
    .uwc-spb__main:focus-visible,
    .uwc-spb__trigger:focus-visible {
      outline:        2px solid var(--_bg);
      outline-offset: 2px;
      z-index:        1;
    }
    .uwc-spb__main[disabled],
    .uwc-spb__trigger[disabled] { pointer-events: none; opacity: 0.6; }

    /* ── Primary action button ───────────────────────────────────────────────── */
    .uwc-spb__main {
      flex:                    1;
      padding-inline:          var(--_px);
      padding-block:           var(--_py);
      border-start-start-radius: var(--_radius);
      border-end-start-radius:   var(--_radius);
      border-start-end-radius:   0;
      border-end-end-radius:     0;
      border-inline-end:         none;
      text-align:                left;
    }
    .uwc-spb__main--loading { cursor: wait; }

    /* ── Chevron trigger button ───────────────────────────────────────────────── */
    .uwc-spb__trigger {
      flex-shrink:               0;
      /* Square-ish chevron — use block padding as inline too */
      padding-inline:            calc(var(--_py) + 0.125rem);
      padding-block:             var(--_py);
      border-start-start-radius: 0;
      border-end-start-radius:   0;
      border-start-end-radius:   var(--_radius);
      border-end-end-radius:     var(--_radius);
      /* Divider — subtle separator between the two halves */
      border-inline-start:       1px solid var(--uwc-spb-divider,
        color-mix(in oklab, var(--_color) 20%, transparent));
    }

    /* ── Outlined ────────────────────────────────────────────────────────────── */
    :host([outlined]) .uwc-spb__main,
    :host([outlined]) .uwc-spb__trigger {
      background-color: transparent;
      border-color:     var(--_bg);
      color:            var(--_bg);
    }
    :host([outlined]) .uwc-spb__trigger {
      border-inline-start-color: color-mix(in oklab, var(--_bg) 40%, transparent);
    }
    :host([outlined]) .uwc-spb__main:hover,
    :host([outlined]) .uwc-spb__trigger:hover {
      background-color: color-mix(in oklab, var(--_bg) 12%, transparent);
    }
    :host([outlined]) .uwc-spb__main:active,
    :host([outlined]) .uwc-spb__trigger:active {
      background-color: color-mix(in oklab, var(--_bg) 20%, transparent);
    }
    :host([outlined]) .uwc-spb__main:focus-visible,
    :host([outlined]) .uwc-spb__trigger:focus-visible {
      outline-color: var(--_bg);
    }

    /* ── Text / Ghost ────────────────────────────────────────────────────────── */
    :host([text]) .uwc-spb__main,
    :host([text]) .uwc-spb__trigger {
      background-color: transparent;
      border-color:     transparent;
      color:            var(--_bg);
    }
    :host([text]) .uwc-spb__trigger {
      border-inline-start: 1px solid
        color-mix(in oklab, var(--_bg) 25%, transparent);
    }
    :host([text]) .uwc-spb__main:hover,
    :host([text]) .uwc-spb__trigger:hover {
      background-color: color-mix(in oklab, var(--_bg) 10%, transparent);
    }
    :host([text]) .uwc-spb__main:active,
    :host([text]) .uwc-spb__trigger:active {
      background-color: color-mix(in oklab, var(--_bg) 18%, transparent);
    }
    :host([text]) .uwc-spb__main:focus-visible,
    :host([text]) .uwc-spb__trigger:focus-visible {
      outline-color: var(--_bg);
    }

    /* ── Inner elements ───────────────────────────────────────────────────────── */
    .uwc-spb__icon  { display: inline-flex; align-items: center; flex-shrink: 0; }
    .uwc-spb__label { display: inline-flex; align-items: center; }

    /* ── Dropdown panel ──────────────────────────────────────────────────────── */
    .uwc-spb__panel {
      position:      fixed;
      inset:         0 auto auto 0;
      margin:        0;
      list-style:    none;
      padding:       var(--uwc-spb-panel-padding, ${t.space1} 0);
      min-width:     var(--uwc-spb-panel-min-width, 10rem);
      background:    var(--uwc-spb-panel-bg, ${t.surface});
      border:        var(--uwc-spb-panel-border, 1px solid ${t.border});
      border-radius: var(--uwc-spb-panel-radius, ${t.radiusMd});
      box-shadow:    var(--uwc-spb-panel-shadow, ${t.shadowLg});
      z-index:       var(--uwc-spb-z, ${t.zFloat});
      opacity:       0;
      transform:     scale(0.97) translateY(-4px);
      transition:
        opacity   var(--uwc-spb-duration, ${t.durationBase}) ${t.easingStandard},
        transform var(--uwc-spb-duration, ${t.durationBase}) ${t.easingStandard},
        display   var(--uwc-spb-duration, ${t.durationBase}) allow-discrete,
        overlay   var(--uwc-spb-duration, ${t.durationBase}) allow-discrete;
    }
    .uwc-spb__panel:popover-open {
      opacity:   1;
      transform: scale(1) translateY(0);
    }
    @starting-style {
      .uwc-spb__panel:popover-open {
        opacity:   0;
        transform: scale(0.97) translateY(-4px);
      }
    }

    /* ── Panel items ─────────────────────────────────────────────────────────── */
    .uwc-spb__item { position: relative; }

    .uwc-spb__item-inner {
      display:         flex;
      align-items:     center;
      gap:             9px;
      width:           100%;
      padding:         var(--uwc-spb-item-padding, ${t.space2} ${t.space4});
      font-size:       var(--uwc-spb-item-font-size, ${t.fontSizeMd});
      font-weight:     ${t.fontWeightMedium};
      font-family:     var(--uwc-font-family, inherit);
      color:           ${t.text};
      background:      transparent;
      border:          none;
      border-radius:   0;
      cursor:          pointer;
      white-space:     nowrap;
      text-align:      left;
      text-decoration: none;
      line-height:     1.4;
      transition:
        background ${t.durationFast} ${t.easingStandard},
        color       ${t.durationFast} ${t.easingStandard};
    }
    .uwc-spb__item-inner:hover,
    .uwc-spb__item-inner:focus-visible {
      background: var(--uwc-spb-item-hover-bg,
        color-mix(in oklab, ${t.text} 7%, transparent));
      outline: none;
    }
    .uwc-spb__item-inner:focus-visible {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }
    .uwc-spb__item-inner[disabled],
    .uwc-spb__item--disabled .uwc-spb__item-inner {
      opacity:        0.45;
      cursor:         not-allowed;
      pointer-events: none;
    }
    .uwc-spb__item--danger .uwc-spb__item-inner {
      color: var(--uwc-spb-item-danger-color, ${t.danger});
    }
    .uwc-spb__item--danger .uwc-spb__item-inner:hover {
      background: color-mix(in oklab, var(--uwc-spb-item-danger-color, ${t.danger}) 10%, transparent);
    }

    .uwc-spb__item-icon {
      display:     inline-flex;
      align-items: center;
      width:       18px;
      flex-shrink: 0;
    }
    .uwc-spb__item-label { flex: 1; }
    .uwc-spb__item-badge {
      font-size:     ${t.fontSizeXs};
      padding:       1px 6px;
      border-radius: ${t.radiusFull};
      background:    ${t.borderSubtle};
      color:         ${t.textSecondary};
      font-weight:   ${t.fontWeightMedium};
      flex-shrink:   0;
    }

    /* ── Separator ───────────────────────────────────────────────────────────── */
    .uwc-spb__separator {
      height:     1px;
      background: ${t.border};
      margin:     ${t.space1} 0;
    }

    /* ── Ripple ──────────────────────────────────────────────────────────────── */
    @keyframes uwc-spb-ripple {
      from { opacity: 0.25; transform: scale(0); }
      to   { opacity: 0;    transform: scale(2.5); }
    }
    .uwc-spb-ripple {
      position:         absolute;
      border-radius:    50%;
      background:       currentColor;
      pointer-events:   none;
      animation:        uwc-spb-ripple 500ms ease-out forwards;
      transform-origin: center;
    }

    /* ── Reduced motion ──────────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-spb__main,
      .uwc-spb__trigger,
      .uwc-spb__item-inner { transition: none; }
      .uwc-spb__panel      { transition: none; }
      .uwc-spb-ripple      { animation: none; }
    }
  `,
];
